/**
 * Body for POST /api/v3/application/initialize/ in the in-store VCC flow.
 *
 * Online flows send the checkout_object the host site gave us. In-store there is no
 * host cart - the shopper builds it on /approved-with-cart - so it is put together here
 * from the in-store cart and the shopper details already in state.
 *
 * The shape follows the backend team's working Postman request for in-store initialize,
 * which differs from the public docs: it needs a top-level `merchandise_amount`, sends
 * item and checkout amounts as strings, and adds `tax` / `item_condition` per item. Leaving
 * any of that out returned a 500.
 */

const toNumber = value => Number(value) || 0;

const toCents = amount => Math.round(toNumber(amount) * 100);

// "1129.99"-style decimal strings, as the Postman request sends amounts.
const toAmount = amount => (toCents(amount) / 100).toFixed(2);

const getQuantity = item => toNumber(item.quantity) || 1;

// Older persisted items stored `warranty` as a bare boolean with no details.
const getWarranty = item => (item.warranty && typeof item.warranty === 'object' ? item.warranty : null);

// The item and its warranty (bought per unit), as counted against the approval.
const getLineCents = item => (toCents(item.price) + toCents(getWarranty(item)?.price)) * getQuantity(item);

// The form has no separate SKU field. Make / Model is what the floor tag carries and is
// what the tele-sales agent cart sends as the item code, so it stands in for the SKU.
const toItem = item => {
  const lineItem = {
    display_name: item.description,
    sku: item.sku || item.makeModel,
    unit_price: toAmount(item.price),
    quantity: String(getQuantity(item)),
    leasable: true,
    tax: '0',
    item_condition: 'new',
  };
  const warranty = getWarranty(item);
  if (warranty) {
    lineItem.warranty = {
      display_name: warranty.name,
      sku: warranty.sku,
      price: toAmount(warranty.price),
    };
  }
  return lineItem;
};

export default function buildInStoreInitializePayload({ newUserDetails = {}, phone, cart = [], code, dedgeToken }) {
  const customerPhone = newUserDetails.phone || phone;
  // Katapult requires billing and shipping to match; in store there is nothing to ship.
  const address = {
    first_name: newUserDetails.billing_first_name,
    last_name: newUserDetails.billing_last_name,
    address: newUserDetails.billing_address,
    ...(newUserDetails.billing_address2 && { address2: newUserDetails.billing_address2 }),
    city: newUserDetails.billing_city,
    state: newUserDetails.billing_state,
    country: 'US',
    zip: newUserDetails.billing_zip,
    phone: customerPhone,
    email: newUserDetails.email,
  };

  return {
    phone: customerPhone,
    code,
    // The whole cart, warranties included - the same total the shopper sees.
    merchandise_amount: cart.reduce((total, item) => total + getLineCents(item), 0) / 100,
    customer: { billing: address, shipping: { ...address } },
    items: cart.map(toItem),
    checkout: { customer_id: '', shipping_amount: '0.00', discounts: [], tax_amount: '0.00' },
    urls: { return: true, cancel: true },
    // Other flows send the device token to initialize when there is one.
    ...(dedgeToken && { dedge_token: dedgeToken }),
  };
}
